// Intake Part 1 step 2 "HM preferences & logistics" (lane B13, DC-231: F-156, F-157; contract C4), inside V1's Part 1
// section form (views/Intake.tsx SectionForm): Preferred contact method · HM availability (Window 30 minutes / 1 hour →
// Day → Start → Add window, the windows listed with Remove) · Work-from-home policy · Relocation package · Travel
// (+ Willing to travel) · Internal candidates / referrals · Logistics note. Native controls only (the bundle budget):
// the date input, a select of half-hour starts and V1's radio choices.

import { useState } from "react";
import { clockNow, getBusinessTimeZone } from "@/api/runtime";
import { inputLengthHint } from "@/api/inputLimits";
import { Field, RequiredMark } from "@/ui";
import { AVAILABILITY_LABELS, CHOICE_LABELS, LOGISTICS_LABELS, atMostWindows } from "./labels";
import {
  CONTACT_METHODS,
  LOGISTICS_TEXT_MAX,
  MAX_WINDOWS,
  RELOCATION_VALUES,
  WFH_VALUES,
  WINDOW_LENGTHS,
  dayIn,
  dayLabel,
  isWorkingDayAfter,
  nextWorkingDay,
  overlaps,
  readAvailability,
  readTravel,
  sortWindows,
  startsFor,
  windowLabel,
  zoneAbbreviation,
  type HmAvailability,
} from "./logistics";

const ID = "preferences_logistics";
const LENGTH_WORD: Record<(typeof WINDOW_LENGTHS)[number], string> = { 30: AVAILABILITY_LABELS.thirtyMinutes, 60: AVAILABILITY_LABELS.oneHour };
const RELOCATION_WORD: Record<(typeof RELOCATION_VALUES)[number], string> = { yes: CHOICE_LABELS.yes, no: CHOICE_LABELS.no, partial: CHOICE_LABELS.partial };
const WFH_WORD: Record<(typeof WFH_VALUES)[number], string> = {
  remote: CHOICE_LABELS.remote,
  office_1: CHOICE_LABELS.office1,
  office_2: CHOICE_LABELS.office2,
  office_3: CHOICE_LABELS.office3,
  office_4: CHOICE_LABELS.office4,
  office_5: CHOICE_LABELS.office5,
};

function Choice<T extends string>({ name, legend, options, value, word, disabled, onPick, describedBy }: { name: string; legend: string; options: readonly T[]; value: T | null; word: (v: T) => string; disabled: boolean; onPick: (v: T) => void; describedBy?: string | undefined }) {
  return (
    <fieldset className="radios" disabled={disabled} aria-describedby={describedBy}>
      <legend>{legend}</legend>
      {options.map((o) => (
        <label key={o} className={`opt${value === o ? " opt--on" : ""}`}>
          <input type="radio" name={name} value={o} checked={value === o} disabled={disabled} onChange={() => onPick(o)} />
          {word(o)}
        </label>
      ))}
    </fieldset>
  );
}

export function LogisticsFields({ values, onChange, canEdit }: { values: Record<string, unknown>; onChange: (values: Record<string, unknown>) => void; canEdit: boolean }) {
  const stored = readAvailability(values["hm_availability"]);
  const availability: HmAvailability = stored ?? { duration_minutes: 30, windows: [] };
  const duration = availability.duration_minutes;
  const windows = availability.windows;
  const now = clockNow();
  const zone = availability.tz ?? getBusinessTimeZone();
  const today = dayIn(now, zone);
  const [day, setDay] = useState("");
  const [start, setStart] = useState("");
  const travel = readTravel(values["travel"]);
  const set = (key: string, value: unknown) => onChange({ ...values, [key]: value });
  const setAvailability = (next: HmAvailability) => set("hm_availability", { duration_minutes: next.duration_minutes, windows: sortWindows(next.windows), ...(availability.tz ? { tz: availability.tz } : {}) });

  const dayOk = day !== "" && isWorkingDayAfter(day, today);
  const dayError = day !== "" && !dayOk ? AVAILABILITY_LABELS.workingDayAfterToday : undefined;
  const full = windows.length >= MAX_WINDOWS;
  const taken = (s: string) => dayOk && windows.some((w) => overlaps(w, { date: day, start: s }, duration));
  const canAdd = canEdit && dayOk && start !== "" && !taken(start) && !full;
  const add = () => {
    if (!canAdd) return;
    setAvailability({ ...availability, windows: [...windows, { date: day, start }] });
    setStart("");
  };
  const abbreviation = zoneAbbreviation(availability.tz, now);
  const note = String(values["additional_logistics_notes"] ?? "");

  return (
    <>
      <Field label={LOGISTICS_LABELS.preferredContactMethod} required id={`${ID}-preferred_contact_method`}>
        <select id={`${ID}-preferred_contact_method`} value={String(values["preferred_contact_method"] ?? "")} disabled={!canEdit} onChange={(e) => set("preferred_contact_method", e.target.value || null)}>
          <option value="">—</option>
          {CONTACT_METHODS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </Field>

      <fieldset className="field availability" id={`${ID}-hm_availability`} aria-describedby={windows.length ? `${ID}-hm_availability-length` : undefined}>
        <legend className="field__label">
          {LOGISTICS_LABELS.hmAvailability}
          <RequiredMark />
        </legend>
        <Choice
          name={`${ID}-window`}
          legend={AVAILABILITY_LABELS.window}
          options={WINDOW_LENGTHS.map(String) as ("30" | "60")[]}
          value={String(duration) as "30" | "60"}
          word={(v) => LENGTH_WORD[Number(v) as 30 | 60]}
          disabled={!canEdit || windows.length > 0}
          describedBy={windows.length ? `${ID}-hm_availability-length` : undefined}
          onPick={(v) => setAvailability({ ...availability, duration_minutes: Number(v) as 30 | 60 })}
        />
        {windows.length > 0 && canEdit && (
          <p className="muted" id={`${ID}-hm_availability-length`}>
            {AVAILABILITY_LABELS.removeEveryWindow}
          </p>
        )}
        {canEdit && (
          <div className="row" style={{ gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Field label={AVAILABILITY_LABELS.day} id={`${ID}-hm_availability-day`} hint={dayError ? undefined : AVAILABILITY_LABELS.workingDayAfterToday} error={dayError}>
              <input id={`${ID}-hm_availability-day`} type="date" min={nextWorkingDay(today)} value={day} onChange={(e) => setDay(e.target.value)} />
            </Field>
            <Field label={AVAILABILITY_LABELS.start} id={`${ID}-hm_availability-start`}>
              <select id={`${ID}-hm_availability-start`} value={start} disabled={!dayOk} onChange={(e) => setStart(e.target.value)}>
                <option value="">—</option>
                {startsFor(duration).map((s) => (
                  <option key={s} value={s} disabled={taken(s)}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <button type="button" className="btn" disabled={!canAdd} aria-describedby={full ? `${ID}-hm_availability-full` : undefined} onClick={add}>
              {AVAILABILITY_LABELS.addWindow}
            </button>
            {full && (
              <span className="muted" id={`${ID}-hm_availability-full`}>
                {atMostWindows({ max: MAX_WINDOWS })}
              </span>
            )}
          </div>
        )}
        {windows.length > 0 && (
          <ul className="plain windows" aria-label={LOGISTICS_LABELS.hmAvailability}>
            {windows.map((w) => (
              <li key={`${w.date}-${w.start}`} className="row" style={{ gap: 8, alignItems: "center" }}>
                <span>{windowLabel(w, duration)}</span>
                {canEdit && (
                  <button type="button" className="btn btn--sm" aria-label={`${AVAILABILITY_LABELS.remove} ${dayLabel(w.date)} ${w.start}`} onClick={() => setAvailability({ ...availability, windows: windows.filter((x) => x !== w) })}>
                    {AVAILABILITY_LABELS.remove}
                  </button>
                )}
              </li>
            ))}
            {abbreviation && <li className="muted">{abbreviation}</li>}
          </ul>
        )}
      </fieldset>

      <Field label={LOGISTICS_LABELS.workFromHomePolicy} id={`${ID}-wfh_policy`}>
        <select id={`${ID}-wfh_policy`} value={WFH_VALUES.includes(values["wfh_policy"] as never) ? String(values["wfh_policy"]) : ""} disabled={!canEdit} onChange={(e) => set("wfh_policy", e.target.value || null)}>
          <option value="">—</option>
          {WFH_VALUES.map((o) => (
            <option key={o} value={o}>
              {WFH_WORD[o]}
            </option>
          ))}
        </select>
      </Field>

      <Choice
        name={`${ID}-relocation`}
        legend={LOGISTICS_LABELS.relocationPackage}
        options={RELOCATION_VALUES}
        value={RELOCATION_VALUES.includes(values["relocation"] as never) ? (values["relocation"] as (typeof RELOCATION_VALUES)[number]) : null}
        word={(v) => RELOCATION_WORD[v]}
        disabled={!canEdit}
        onPick={(v) => set("relocation", v)}
      />

      <Choice
        name={`${ID}-travel`}
        legend={LOGISTICS_LABELS.travel}
        options={["yes", "no"] as const}
        value={travel ? (travel.required ? "yes" : "no") : null}
        word={(v) => (v === "yes" ? CHOICE_LABELS.yes : CHOICE_LABELS.no)}
        disabled={!canEdit}
        onPick={(v) => set("travel", v === "yes" ? { required: true, percent: travel?.required ? travel.percent : null } : { required: false, percent: null })}
      />
      {travel?.required && (
        <Field label={CHOICE_LABELS.willingToTravel} id={`${ID}-travel_percent`}>
          <span className="row" style={{ gap: 6, alignItems: "center" }}>
            <input
              id={`${ID}-travel_percent`}
              type="number"
              min={0}
              max={100}
              step={5}
              value={travel.percent ?? ""}
              disabled={!canEdit}
              onChange={(e) => set("travel", { required: true, percent: e.target.value === "" ? null : Number(e.target.value) })}
            />
            <span aria-hidden="true">%</span>
          </span>
        </Field>
      )}

      <Field label={LOGISTICS_LABELS.internalCandidates} id={`${ID}-internal_candidates_policy`}>
        <textarea id={`${ID}-internal_candidates_policy`} rows={3} maxLength={LOGISTICS_TEXT_MAX} value={String(values["internal_candidates_policy"] ?? "")} disabled={!canEdit} onChange={(e) => set("internal_candidates_policy", e.target.value)} />
      </Field>
      <Field label={LOGISTICS_LABELS.logisticsNote} id={`${ID}-additional_logistics_notes`} hint={note.length > LOGISTICS_TEXT_MAX * 0.9 ? inputLengthHint(note, LOGISTICS_TEXT_MAX) : undefined}>
        <textarea id={`${ID}-additional_logistics_notes`} rows={4} maxLength={LOGISTICS_TEXT_MAX} value={note} disabled={!canEdit} onChange={(e) => set("additional_logistics_notes", e.target.value)} />
      </Field>
    </>
  );
}
